import React, { Fragment } from 'react'
import {
  BasisConvention,
  Collateral,
  CollateralIdentifierType,
  CollateralIndexDetails,
  CollateralPrice,
  CollateralType,
  Market,
  Product,
  RefinitivAssetType,
  TradingType,
  UserPreference,
  ValidationIssue
} from 'api'
import { DatePickerField, DialogField, DialogRow, NumericInputField, TextInputField } from 'view/components/inputs'
import {
  BooleanSelectboxField,
  CouponFrequencySelectboxField,
  CouponTypeSelectboxField,
  CurrencySelectboxField,
  IndexSelectboxField,
  ProductSectorSelectboxField,
  TradingTypeSelectboxField
} from 'view/shared/entity'
import * as constants from 'view/styles/constants'
import { CollateralTypeSelectboxField } from 'view/shared/entity/CollateralTypeSelectbox'
import { IndexLagSelectboxField } from 'view/shared/entity/IndexLagSelectbox'
import { CollateralIdentifierTypeSelectboxField } from 'view/shared/entity/CollateralIdentifierTypeSelectbox'
import { BasisConventionSelectboxField } from 'view/shared/entity/BasisConventionSelectbox'
import { noop } from 'lodash'
import { SenioritySelectboxField } from 'view/shared/entity/SenioritySelectbox'
import { formatDateAndTime } from 'locale/Locale'
import { Locale, Translation } from 'locale'
import { useSelector } from 'react-redux'
import { createUserNameByUserIdGetterSelector } from 'selectors/userSelectors'
import { ReadonlyFields } from './CollateralDialogComponent'
import { Nullable } from 'utils'
import { CollateralDialogFields } from './collateralDialogUtils'
import { AssetTypeSelectboxField } from 'view/shared/entity/AssetTypeSelectbox'
import { CountrySelectboxField } from 'view/shared/entity/CountrySelectbox'
import { CollateralStatusSelectbox } from './CollateralStatusSelectbox'
import { IssueSelectboxField } from 'view/shared/entity/IssueSelectbox'
import { RefinitivAssetTypeSelectboxField } from 'view/shared/entity/RefinitivAssetTypeSelectbox'
import { useUpdateHandler } from 'view/hooks'
import { createCollateralDetailsByIdGetterSelector } from 'selectors/collateralSelectors'
import { AssetTypeHelper } from 'model'
import { complianceValidFormatter } from 'view/shared/datagrid'

interface CollateralMainDetailsPanelProps {
  originalCollateral: Nullable<Collateral>
  collateral: Collateral
  collateralPrice: CollateralPrice
  product: Nullable<Product>
  userPreference: UserPreference
  readOnly: ReadonlyFields
  validationIssues: ValidationIssue[]
  collateralPriceValidationIssues: ValidationIssue[]
  onUpdateCollateral: (partialCollateral: Partial<Collateral>) => void
  onUpdateCollateralPrice: (cleanPrice: number, collateralId?: string) => void
}

export const CollateralMainDetailsPanel: React.FC<CollateralMainDetailsPanelProps> = ({
  originalCollateral,
  collateral,
  collateralPrice,
  product,
  userPreference,
  readOnly,
  validationIssues,
  collateralPriceValidationIssues,
  onUpdateCollateral,
  onUpdateCollateralPrice
}: CollateralMainDetailsPanelProps) => {
  const getCollateralDetailsById = useSelector(createCollateralDetailsByIdGetterSelector)
  const collateralDetails = getCollateralDetailsById(collateral.id)

  const updateHandler = useUpdateHandler(onUpdateCollateral)

  const issueDateUpdateHandler = (value: string) => {
    onUpdateCollateral({ issueDate: value })
  }

  const collateralTypeUpdateHandler = (value: CollateralType) => {
    onUpdateCollateral({ collateralType: value, collateralIndexDetails: null })
  }

  const basisConventionUpdateHandler = (value: BasisConvention) => {
    onUpdateCollateral({ basisConvention: value, collateralIndexDetails: null })
  }

  const collateralIdentifierUpdateHandler = (updatingCollateralIdentifierType: CollateralIdentifierType) => (
    isinOrCusip: Collateral['isin' | 'cusip']
  ) => {
    const shouldUpdateCollateralId = collateral.collateralIdentifierType === updatingCollateralIdentifierType
    onUpdateCollateral({
      ...(shouldUpdateCollateralId ? { id: isinOrCusip ?? undefined } : undefined),
      [updatingCollateralIdentifierType === CollateralIdentifierType.ISIN ? 'isin' : 'cusip']: isinOrCusip
    })
    onUpdateCollateralPrice(collateralPrice.cleanPrice, shouldUpdateCollateralId ? isinOrCusip ?? undefined : undefined)
  }

  const collateralIdentifierTypeUpdateHandler = (collateralIdentifierType: CollateralIdentifierType) => {
    const isIsin = collateralIdentifierType === CollateralIdentifierType.ISIN
    const id = (isIsin ? collateral.isin : collateral.cusip) || undefined
    const tradingType = isIsin ? TradingType.NOTIONAL : TradingType.EITHER
    onUpdateCollateral({
      id,
      collateralIdentifierType,
      tradingType,
      productSectorId: undefined
    })
    onUpdateCollateralPrice(collateralPrice.cleanPrice, id)
  }

  const collateralIndexDetailsUpdateHandler = <K extends keyof CollateralIndexDetails>(key: K) => (value: CollateralIndexDetails[K]) => {
    const collateralIndexDetails = collateral.collateralIndexDetails ?? {
      indexId: null,
      indexLag: null,
      baseIndexValue: null
    }
    onUpdateCollateral({ collateralIndexDetails: { ...collateralIndexDetails, [key]: value } })
  }

  const getUserNameFromUserId = useSelector(createUserNameByUserIdGetterSelector)
  const collateralMarket = collateral.collateralIdentifierType
    ? collateral.collateralIdentifierType === CollateralIdentifierType.ISIN
      ? Market.EUROPE
      : Market.US
    : undefined

  return (
    <Fragment>
      <DialogRow>
        <DialogField>
          <CollateralStatusSelectbox
            collateral={collateral}
            onUpdate={value => onUpdateCollateral({ status: value })}
            originalCollateral={originalCollateral}
            readOnly={readOnly.status}
            validationIssues={validationIssues}
          />
        </DialogField>
        <DialogField>
          <TextInputField
            dataTestId={'CollateralDialog-TextInputField-EUCompliance-Status'}
            label={Locale.translate(Translation.TC_COMPLIANCE_EUROPE)}
            value={complianceValidFormatter(collateral.euCompliant)}
            readOnly={true}
            onUpdate={noop}
          />
        </DialogField>
      </DialogRow>
      <DialogRow>
        <DialogField>
          <TextInputField
            dataTestId={'CollateralDialog-TextInputField-CollateralIsin'}
            fieldName={CollateralDialogFields.ISIN}
            validationField={['isin', ...(collateral.collateralIdentifierType === CollateralIdentifierType.ISIN ? ['id'] : [])]}
            label={Locale.translate(Translation.SC_ISIN)}
            value={collateral.isin}
            maxLength={constants.isinMaxFieldLength}
            readOnly={readOnly.isin}
            onUpdate={collateralIdentifierUpdateHandler(CollateralIdentifierType.ISIN)}
            validationIssues={validationIssues || undefined}
            autoCapitalise={true}
          />
        </DialogField>
        <DialogField>
          <TextInputField
            dataTestId={'CollateralDialog-TextInputField-CollateralRic'}
            fieldName={CollateralDialogFields.RIC}
            label={Locale.translate(Translation.SC_RIC)}
            value={collateral.ric}
            maxLength={constants.ricMaxFieldLength}
            readOnly={readOnly.ric}
            onUpdate={updateHandler('ric')}
            validationIssues={validationIssues || undefined}
          />
        </DialogField>
        <DialogField>
          <TextInputField
            dataTestId={'CollateralDialog-TextInputField-CollateralCusip'}
            fieldName={CollateralDialogFields.CUSIP}
            validationField={[
              CollateralDialogFields.CUSIP,
              ...(collateral.collateralIdentifierType === CollateralIdentifierType.CUSIP ? ['id'] : [])
            ]}
            label={Locale.translate(Translation.SC_CUSIP)}
            value={collateral.cusip}
            maxLength={constants.cusipMaxFieldLength}
            readOnly={readOnly.cusip}
            onUpdate={collateralIdentifierUpdateHandler(CollateralIdentifierType.CUSIP)}
            validationIssues={validationIssues || undefined}
            autoCapitalise={true}
          />
        </DialogField>
      </DialogRow>
      <DialogRow verticalLayout={constants.VerticalLayout.Bottom}>
        <DialogField>
          <CollateralIdentifierTypeSelectboxField
            dataTestId={'CollateralDialog-CollateralIdentifierTypeSelectboxField-CollateralIdentifierType'}
            fieldName={CollateralDialogFields.COLLATERAL_IDENTIFIER_TYPE}
            label={Locale.translate(Translation.SC_PLATFORM)}
            value={collateral.collateralIdentifierType}
            readOnly={readOnly.collateralIdentifierType}
            onUpdate={collateralIdentifierTypeUpdateHandler}
            validationIssues={validationIssues || undefined}
          />
        </DialogField>
        <DialogField>
          <ProductSectorSelectboxField
            dataTestId={'CollateralDialog-ProductSectorSelectboxField-CollateralSector'}
            fieldName={CollateralDialogFields.PRODUCT_SECTOR}
            label={Locale.translate(Translation.SC_SECTOR)}
            value={collateral.productSectorId}
            readOnly={readOnly.productSectorId}
            onUpdate={updateHandler(CollateralDialogFields.PRODUCT_SECTOR)}
            validationIssues={validationIssues || undefined}
            market={collateralMarket}
          />
        </DialogField>
        <DialogField>
          <TextInputField
            dataTestId={'CollateralDialog-TextInputField-CollateralProduct'}
            label={Locale.translate(Translation.SC_PRODUCT)}
            value={product && product.name}
            readOnly={readOnly.product}
            onUpdate={noop}
          />
        </DialogField>
      </DialogRow>
      <DialogRow>
        <DialogField>
          <AssetTypeSelectboxField
            dataTestId={'CollateralDialog-AssetTypeSelectboxField-AssetType'}
            fieldName={CollateralDialogFields.ASSET_TYPE}
            label={Locale.translate(Translation.TC_ASSET_TYPE)}
            value={collateral.assetType}
            readOnly={readOnly.assetType}
            onUpdate={updateHandler(CollateralDialogFields.ASSET_TYPE)}
            validationIssues={validationIssues || undefined}
            canClear={true}
            getOptionText={AssetTypeHelper.getName}
          />
        </DialogField>
        <DialogField>
          <RefinitivAssetTypeSelectboxField
            dataTestId={'CollateralDialog-RefinitivAssetTypeSelectboxField'}
            fieldName={CollateralDialogFields.REFINITIV_ASSET_TYPE}
            label={Locale.translate(Translation.TC_REFINITIV_ASSET_TYPE)}
            value={(collateral.refinitivAssetType as unknown) as RefinitivAssetType}
            readOnly={readOnly.refinitivAssetType}
            onUpdate={noop}
          />
        </DialogField>
        <DialogField>
          <TextInputField
            dataTestId={'CollateralDialog-TextInputField-AssetSubType'}
            fieldName={CollateralDialogFields.ASSET_SUB_TYPE}
            label={Locale.translate(Translation.TC_ASSET_SUB_TYPE)}
            value={collateral.assetSubType}
            readOnly={readOnly.assetSubType}
            onUpdate={noop}
            autoCapitalise={true}
          />
        </DialogField>
        <DialogField>
          <TextInputField
            dataTestId={'CollateralDialog-TextInputField-DebtType'}
            fieldName={CollateralDialogFields.DEBT_TYPE}
            label={Locale.translate(Translation.TC_ASSET_DEBT_TYPE)}
            value={collateral.debtType}
            readOnly={readOnly.debtType}
            onUpdate={noop}
            autoCapitalise={true}
          />
        </DialogField>
      </DialogRow>
      <DialogRow>
        <DialogField>
          <TextInputField
            dataTestId={'CollateralDialog-TextInputField-CollateralShortId'}
            fieldName={CollateralDialogFields.NAME}
            label={Locale.translate(Translation.SC_COLLATERAL_SHORT_ID)}
            value={collateral.name}
            maxLength={constants.nameMaxFieldLength}
            readOnly={readOnly.name}
            onUpdate={updateHandler('name')}
            validationIssues={validationIssues || undefined}
          />
        </DialogField>
        <DialogField flex={2}>
          <TextInputField
            dataTestId={'CollateralDialog-TextInputField-CollateralFisin'}
            fieldName={CollateralDialogFields.FISIN}
            label={Locale.translate(Translation.SC_FISN)}
            value={collateral.fisin}
            maxLength={constants.fisnMaxFieldLength}
            readOnly={readOnly.fisin}
            onUpdate={updateHandler(CollateralDialogFields.FISIN)}
            validationIssues={validationIssues || undefined}
          />
        </DialogField>
        <DialogField>
          <TextInputField
            dataTestId={'CollateralDialog-TextInputField-CollateralCfi'}
            fieldName={CollateralDialogFields.CFI}
            label={Locale.translate(Translation.SC_CFI)}
            value={collateral.cfi}
            maxLength={constants.cfiMaxFieldLength}
            readOnly={readOnly.cfi}
            onUpdate={updateHandler(CollateralDialogFields.CFI)}
            validationIssues={validationIssues || undefined}
          />
        </DialogField>
      </DialogRow>
      <DialogRow>
        <DialogField>
          <IssueSelectboxField
            canClear
            dataTestId={'CollateralDialog-IssueSelectboxField-Issue'}
            fieldName={CollateralDialogFields.ISSUE}
            label={Locale.translate(Translation.TC_ISSUE)}
            value={collateral.issue}
            readOnly={readOnly.issue}
            onUpdate={updateHandler(CollateralDialogFields.ISSUE)}
            validationIssues={validationIssues || undefined}
          />
        </DialogField>
        <DialogField>
          <CurrencySelectboxField
            dataTestId={'CollateralDialog-CurrencySelectboxField-CollateralCurrency'}
            fieldName={CollateralDialogFields.CURRENCY}
            label={Locale.translate(Translation.SC_CURRENCY)}
            value={collateral.currency}
            readOnly={readOnly.currency}
            onUpdate={updateHandler(CollateralDialogFields.CURRENCY)}
            validationIssues={validationIssues || undefined}
          />
        </DialogField>
      </DialogRow>
      <DialogRow>
        <DialogField>
          <CountrySelectboxField
            dataTestId={'CollateralDialog-CountrySelectboxField-CollateralCountry'}
            fieldName={CollateralDialogFields.COUNTRY_CODE}
            label={Locale.translate(Translation.SC_COUNTRY)}
            value={collateral.countryCode}
            readOnly={readOnly.countryCode}
            onUpdate={updateHandler(CollateralDialogFields.COUNTRY_CODE)}
            validationIssues={validationIssues || undefined}
          />
        </DialogField>
        <DialogField>
          <TextInputField
            dataTestId={'CollateralAdditionalDetailsPanel-CountryOfIssuance'}
            fieldName={CollateralDialogFields.COUNTRY_OF_ISSUANCE}
            label={Locale.translate(Translation.TC_COUNTRY_OF_ISSUANCE)}
            value={collateral.countryOfIssuance}
            readOnly={readOnly.countryOfIssuance}
            onUpdate={noop}
            validationIssues={validationIssues || undefined}
          />
        </DialogField>
        <DialogField>
          <TextInputField
            dataTestId={'CollateralAdditionalDetailsPanel-CountryOfIncorporation'}
            fieldName={CollateralDialogFields.COUNTRY_OF_INCORPORATION}
            label={Locale.translate(Translation.TC_COUNTRY_OF_INCORPORATION)}
            value={collateral.countryOfIncorporation}
            readOnly={readOnly.countryOfIncorporation}
            onUpdate={noop}
            validationIssues={validationIssues || undefined}
          />
        </DialogField>
      </DialogRow>
      <DialogRow>
        <DialogField>
          <TradingTypeSelectboxField
            dataTestId={'CollateralDialog-NumericInputField-CollateralTradingType'}
            fieldName={CollateralDialogFields.TRADING_TYPE}
            label={Locale.translate(Translation.SC_TRADING_TYPE)}
            value={collateral.tradingType}
            readOnly={readOnly.tradingType}
            onUpdate={updateHandler(CollateralDialogFields.TRADING_TYPE)}
            validationIssues={validationIssues || undefined}
          />
        </DialogField>
        <DialogField>
          <NumericInputField
            dataTestId={'CollateralDialog-NumericInputField-CollateralCleanPrice'}
            fieldName={CollateralDialogFields.CLEAN_PRICE}
            label={Locale.translate(Translation.SC_CLEAN_PRICE)}
            value={collateralPrice?.cleanPrice}
            maxLength={constants.defaultMaxTextFieldLength}
            formatOptions={{ minDp: 3 }}
            readOnly={readOnly.cleanPrice}
            onUpdate={(value: number) => onUpdateCollateralPrice(value)}
            validationIssues={collateralPriceValidationIssues || undefined}
          />
        </DialogField>
        <DialogField>
          <NumericInputField
            dataTestId={'CollateralDialog-NumericInputField-MinimumTicketSize'}
            fieldName={CollateralDialogFields.MINIMUM_TICKET_SIZE}
            label={Locale.translate(Translation.TC_MINIMUM_TICKET_SIZE)}
            value={collateral.minimumTicketSize}
            maxLength={constants.defaultMaxTextFieldLength}
            formatOptions={{ minDp: 2, separator: true }}
            readOnly={readOnly.minimumTicketSize}
            onUpdate={updateHandler(CollateralDialogFields.MINIMUM_TICKET_SIZE)}
            validationIssues={validationIssues || undefined}
          />
        </DialogField>
        <DialogField>
          <NumericInputField
            dataTestId={'CollateralDialog-NumericInputField-CollateralTicketSize'}
            fieldName={CollateralDialogFields.TICKET_INCREMENT}
            label={Locale.translate(Translation.TC_TICKET_SIZE_INCREMENT)}
            value={collateral.ticketIncrement}
            maxLength={constants.defaultMaxTextFieldLength}
            formatOptions={{ minDp: 2, separator: true }}
            readOnly={readOnly.ticketIncrement}
            onUpdate={updateHandler(CollateralDialogFields.TICKET_INCREMENT)}
            validationIssues={validationIssues || undefined}
          />
        </DialogField>
      </DialogRow>
      <DialogRow>
        <DialogField>
          <TextInputField
            dataTestId={'CollateralDialog-TextInputField-CollateralIssuer'}
            fieldName={CollateralDialogFields.ISSUER}
            label={Locale.translate(Translation.SC_ISSUER)}
            value={collateral.issuer}
            maxLength={constants.defaultMaxTextFieldLength}
            readOnly={readOnly.issuer}
            onUpdate={updateHandler(CollateralDialogFields.ISSUER)}
            validationIssues={validationIssues || undefined}
          />
        </DialogField>
        <DialogField>
          <TextInputField
            dataTestId={'CollateralDialog-TextInputField-CollateralIssuerLei'}
            fieldName={CollateralDialogFields.ISSUER_LEI}
            label={Locale.translate(Translation.TC_ISSUER_LEI)}
            value={collateral.issuerLei}
            maxLength={constants.leiMaxFieldLength}
            readOnly={readOnly.issuerLei}
            onUpdate={updateHandler(CollateralDialogFields.ISSUER_LEI)}
            validationIssues={validationIssues || undefined}
          />
        </DialogField>
        <DialogField>
          <DatePickerField
            dataTestId={'CollateralDialog-DatePickerField-CollateralFirstSettlementDate'}
            fieldName={CollateralDialogFields.ISSUE_DATE}
            label={Locale.translate(Translation.SC_FIRST_SETTLEMENT_DATE)}
            value={collateral.issueDate}
            readOnly={readOnly.issueDate}
            onUpdate={issueDateUpdateHandler}
            validationIssues={validationIssues || undefined}
          />
        </DialogField>
      </DialogRow>
      <DialogRow>
        <DialogField>
          <DatePickerField
            dataTestId={'CollateralDialog-DatePickerField-CollateralAccrualDate'}
            fieldName={CollateralDialogFields.COUPON_ACCRUED_INTEREST_DATE}
            label={Locale.translate(Translation.SC_ACCRUAL_DATE)}
            value={collateral.couponAccruedInterestDate}
            readOnly={readOnly.couponAccruedInterestDate}
            onUpdate={updateHandler(CollateralDialogFields.COUPON_ACCRUED_INTEREST_DATE)}
            validationIssues={validationIssues || undefined}
          />
        </DialogField>
        <DialogField>
          <DatePickerField
            dataTestId={'CollateralDialog-DatePickerField-CollateralFirstCouponDate'}
            fieldName={CollateralDialogFields.COUPON_FIRST_DATE}
            label={Locale.translate(Translation.SC_FIRST_COUPON_DATE)}
            value={collateral.couponFirstDate}
            readOnly={readOnly.couponFirstDate}
            onUpdate={updateHandler(CollateralDialogFields.COUPON_FIRST_DATE)}
            validationIssues={validationIssues || undefined}
          />
        </DialogField>
        <DialogField>
          <DatePickerField
            dataTestId={'CollateralDialog-DatePickerField-CollateralMaturityDate'}
            fieldName={CollateralDialogFields.COUPON_MATURITY_DATE}
            label={Locale.translate(Translation.SC_MATURITY_DATE)}
            value={collateral.couponMaturityDate}
            minDate={new Date()}
            readOnly={readOnly.couponMaturityDate}
            onUpdate={updateHandler(CollateralDialogFields.COUPON_MATURITY_DATE)}
            validationIssues={validationIssues || undefined}
          />
        </DialogField>
      </DialogRow>
      <DialogRow>
        <DialogField>
          <TextInputField
            dataTestId={'CollateralDialog-DatePickerField-FirstTradeInstant'}
            fieldName={CollateralDialogFields.FIRST_TRADE_INSTANT}
            label={Locale.translate(Translation.SC_FIRST_TRADE_DATE)}
            value={formatDateAndTime(collateral.firstTradeTimestamp, userPreference.dateFormat)}
            readOnly={readOnly.firstTradeInstant}
            onUpdate={noop}
            validationIssues={validationIssues || undefined}
          />
        </DialogField>
        <DialogField>
          <CouponTypeSelectboxField
            dataTestId={'CollateralDialog-CouponTypeSelectboxField-CollateralCouponType'}
            fieldName={CollateralDialogFields.COUPON_TYPE}
            label={Locale.translate(Translation.SC_COUPON_TYPE)}
            value={collateral.couponType}
            readOnly={readOnly.couponType}
            onUpdate={updateHandler('couponType')}
            validationIssues={validationIssues || undefined}
          />
        </DialogField>
        <DialogField>
          <BasisConventionSelectboxField
            dataTestId={'CollateralDialog-BasisConventionSelectboxField-CollateralBasisConvention'}
            fieldName={CollateralDialogFields.BASIS_CONVENTION}
            label={Locale.translate(Translation.SC_BASIS_CONVENTION)}
            value={collateral.basisConvention}
            readOnly={readOnly.basisConvention}
            onUpdate={basisConventionUpdateHandler}
            validationIssues={validationIssues || undefined}
          />
        </DialogField>
      </DialogRow>
      <DialogRow>
        <DialogField>
          <NumericInputField
            dataTestId={'CollateralDialog-NumericInputField-CollateralCoupon'}
            fieldName={CollateralDialogFields.COUPON}
            label={Locale.translate(Translation.SC_COUPON)}
            value={collateral.coupon}
            maxLength={constants.defaultMaxTextFieldLength}
            formatOptions={{ minDp: 1, percent: true, decimalShift: 2 }}
            readOnly={readOnly.coupon}
            onUpdate={updateHandler(CollateralDialogFields.COUPON)}
            validationIssues={validationIssues || undefined}
          />
        </DialogField>
        <DialogField>
          <CouponFrequencySelectboxField
            dataTestId={'CollateralDialog-CouponFrequencySelectboxField-CollateralFrequency'}
            fieldName={CollateralDialogFields.COUPON_FREQUENCY}
            label={Locale.translate(Translation.SC_FREQUENCY)}
            value={collateral.couponFrequency}
            readOnly={readOnly.couponFrequency}
            onUpdate={updateHandler(CollateralDialogFields.COUPON_FREQUENCY)}
            validationIssues={validationIssues || undefined}
          />
        </DialogField>
        <DialogField>
          <NumericInputField
            dataTestId={'CollateralDialog-NumericInputField-TotalIssuedNominalAmount'}
            fieldName={CollateralDialogFields.TOTAL_ISSUED_NOMINAL_AMOUNT}
            label={Locale.translate(Translation.SC_TOTAL_ISSUED_NOMINAL_AMOUNT)}
            value={collateral.totalIssuedNominalAmount}
            maxLength={constants.defaultMaxTextFieldLength}
            formatOptions={{ maxDp: 2 }}
            readOnly={readOnly.totalIssuedNominalAmount}
            onUpdate={updateHandler(CollateralDialogFields.TOTAL_ISSUED_NOMINAL_AMOUNT)}
            validationIssues={validationIssues || undefined}
          />
        </DialogField>
      </DialogRow>
      <DialogRow>
        <DialogField>
          <BooleanSelectboxField
            dataTestId={'CollateralDialog-BooleanSelectbox-InEsmaFirds'}
            fieldName={CollateralDialogFields.IN_ESMA_FIRDS}
            label={Locale.translate(Translation.TC_IN_ESMA_FIRDS)}
            value={collateral.inEsmaFirds}
            readOnly={readOnly.inEsmaFirds}
            onUpdate={noop}
            validationIssues={validationIssues || undefined}
          />
        </DialogField>
        <DialogField>
          <BooleanSelectboxField
            dataTestId={'CollateralDialog-BooleanSelectbox-EeaVenueEligible'}
            fieldName={CollateralDialogFields.EEA_VENUE_ELIGIBLE}
            label={Locale.translate(Translation.TC_EEA_VENUE_ELIGIBLE)}
            value={collateral.eeaVenueEligible}
            readOnly={readOnly.eeaVenueEligible}
            onUpdate={noop}
            validationIssues={validationIssues || undefined}
          />
        </DialogField>
        <DialogField>
          <BooleanSelectboxField
            dataTestId={'CollateralDialog-BooleanSelectbox-UkVenueEligible'}
            fieldName={CollateralDialogFields.UK_VENUE_ELIGIBLE}
            label={Locale.translate(Translation.TC_UK_VENUE_ELIGIBLE)}
            value={collateral.ukVenueEligible}
            readOnly={readOnly.ukVenueEligible}
            onUpdate={noop}
            validationIssues={validationIssues || undefined}
          />
        </DialogField>
      </DialogRow>
      <DialogRow>
        <DialogField>
          <BooleanSelectboxField
            dataTestId={'CollateralDialog-Rule144aIndicator'}
            fieldName={CollateralDialogFields.RULE_144A_INDICATOR}
            label={Locale.translate(Translation.TC_RULE_144A_INDICATOR)}
            value={collateral.rule144aIndicator}
            readOnly={readOnly.rule144aIndicator}
            onUpdate={noop}
            validationIssues={validationIssues || undefined}
          />
        </DialogField>
        <DialogField>
          <TextInputField
            dataTestId={'CollateralDialog-RegulationIndicator'}
            fieldName={CollateralDialogFields.REGULATION_INDICATOR}
            label={Locale.translate(Translation.TC_REGULATION_S_INDICATOR)}
            value={collateralDetails?.regulationIndicator}
            readOnly={readOnly.regulationIndicator}
            onUpdate={noop}
            validationIssues={validationIssues || undefined}
          />
        </DialogField>
        <DialogField>
          <TextInputField
            dataTestId={'CollateralDialog-IssuerOrgId'}
            fieldName={CollateralDialogFields.ISSUE_ORG_ID}
            label={Locale.translate(Translation.TC_ISSUER_ORG_ID)}
            value={collateral.issuerOrgId}
            readOnly={readOnly.issuerOrgId}
            onUpdate={noop}
            validationIssues={validationIssues || undefined}
          />
        </DialogField>
        <DialogField>
          <BooleanSelectboxField
            dataTestId={'CollateralDialog-ComplianceOverride'}
            fieldName={CollateralDialogFields.COMPLIANCE_OVERRIDE}
            label={Locale.translate(Translation.TC_COMPLIANCE_OVERRIDE)}
            value={collateral.complianceOverride}
            readOnly={readOnly.complianceOverride}
            onUpdate={updateHandler(CollateralDialogFields.COMPLIANCE_OVERRIDE)}
            validationIssues={validationIssues || undefined}
          />
        </DialogField>
      </DialogRow>
      <DialogRow>
        <DialogField>
          <NumericInputField
            dataTestId={'CollateralDialog-TextInputField-CollateralFactor'}
            fieldName={CollateralDialogFields.FACTOR}
            label={Locale.translate(Translation.TC_FACTOR)}
            value={collateral.factor}
            formatOptions={{ maxFractionDigits: 10 }}
            min={0}
            readOnly={readOnly.factor}
            onUpdate={updateHandler(CollateralDialogFields.FACTOR)}
            validationIssues={validationIssues || undefined}
          />
        </DialogField>
        <DialogField>
          <DatePickerField
            dataTestId={'CollateralDialog-DatePickerField-CollateralFactorDate'}
            fieldName={CollateralDialogFields.FACTOR_DATE}
            label={Locale.translate(Translation.TC_FACTOR_DATE)}
            value={collateral.factorDate}
            readOnly={readOnly.factorDate}
            onUpdate={updateHandler(CollateralDialogFields.FACTOR_DATE)}
            validationIssues={validationIssues || undefined}
          />
        </DialogField>

        <DialogField>
          <DatePickerField
            dataTestId={'CollateralDialog-DatePickerField-CollateralMBSPriceDate'}
            fieldName={CollateralDialogFields.MBS_PRICE_DATE}
            label={Locale.translate(Translation.TC_MBS_PRICE_DATE)}
            value={collateral.mbsPriceDate}
            readOnly={true}
            validationIssues={validationIssues || undefined}
            onUpdate={noop}
          />
        </DialogField>
      </DialogRow>
      <DialogRow>
        <DialogField>
          <CollateralTypeSelectboxField
            dataTestId={'CollateralDialog-CollateralTypeSelectboxField-CollateralType'}
            fieldName={CollateralDialogFields.COLLATERAL_TYPE}
            label={Locale.translate(Translation.SC_COLLATERAL_TYPE)}
            value={collateral.collateralType}
            readOnly={readOnly.collateralType}
            onUpdate={collateralTypeUpdateHandler}
            validationIssues={validationIssues || undefined}
          />
        </DialogField>
        <DialogField>
          <IndexLagSelectboxField
            dataTestId={'CollateralDialog-IndexLagSelectboxField-CollateralIndexLag'}
            fieldName={CollateralDialogFields.INDEX_LAG}
            label={Locale.translate(Translation.SC_COLLATERAL_INDEX_LAG)}
            value={collateral.collateralIndexDetails?.indexLag}
            readOnly={readOnly.indexLag}
            onUpdate={collateralIndexDetailsUpdateHandler(CollateralDialogFields.INDEX_LAG)}
            validationIssues={validationIssues || undefined}
          />
        </DialogField>
        <DialogField>
          <BooleanSelectboxField
            dataTestId={'CollateralDialog-BooleanSelectbox-Prospectus'}
            fieldName={CollateralDialogFields.PROSPECTUS}
            label={Locale.translate(Translation.SC_PROSPECTUS)}
            value={collateral.prospectus}
            readOnly={readOnly.prospectus}
            onUpdate={noop}
            validationIssues={validationIssues || undefined}
          />
        </DialogField>
        <DialogField>
          <SenioritySelectboxField
            dataTestId={'CollateralDialog-SenioritySelectboxField-CollateralSeniority'}
            fieldName={CollateralDialogFields.SENIORITY}
            label={Locale.translate(Translation.SC_SENIORITY)}
            value={collateral.seniority}
            readOnly={readOnly.seniority}
            onUpdate={updateHandler(CollateralDialogFields.SENIORITY)}
            validationIssues={validationIssues || undefined}
          />
        </DialogField>
      </DialogRow>
      <DialogRow>
        <DialogField>
          <IndexSelectboxField
            dataTestId={'CollateralDialog-IndexSelectboxField-CollateralIndex'}
            fieldName={CollateralDialogFields.INDEX_ID}
            label={Locale.translate(Translation.SC_COLLATERAL_INDEX)}
            value={collateral.collateralIndexDetails?.indexId}
            readOnly={readOnly.indexId}
            onUpdate={collateralIndexDetailsUpdateHandler(CollateralDialogFields.INDEX_ID)}
            validationIssues={validationIssues || undefined}
          />
        </DialogField>
        <DialogField>
          <TextInputField
            dataTestId={'CollateralDialog-IndexSelectboxField-RefinitivIndex'}
            fieldName={CollateralDialogFields.REFINITIV_BASE_INDEX}
            label={Locale.translate(Translation.TC_REFINITIV_BASE_INDEX)}
            value={collateral.refinitivBaseIndex}
            readOnly={readOnly.refinitivBaseIndex}
            onUpdate={noop}
            validationIssues={validationIssues || undefined}
          />
        </DialogField>

        <DialogField>
          <NumericInputField
            dataTestId={'CollateralDialog-NumericInputField-IndexSpread'}
            fieldName={CollateralDialogFields.INDEX_SPREAD}
            label={Locale.translate(Translation.SC_INDEX_SPREAD)}
            value={collateral.indexSpread}
            maxLength={constants.defaultMaxTextFieldLength}
            readOnly={readOnly.indexSpread}
            onUpdate={updateHandler(CollateralDialogFields.INDEX_SPREAD)}
            validationIssues={validationIssues || undefined}
          />
        </DialogField>
        <DialogField>
          <NumericInputField
            dataTestId={'CollateralDialog-NumericInputField-CollateralBaseIndexValue'}
            fieldName={CollateralDialogFields.BASE_INDEX_VALUE}
            label={Locale.translate(Translation.SC_COLLATERAL_BASE_INDEX_VALUE)}
            value={collateral.collateralIndexDetails?.baseIndexValue}
            maxLength={constants.defaultMaxTextFieldLength}
            formatOptions={{ minDp: 6 }}
            readOnly={readOnly.baseIndexValue}
            onUpdate={collateralIndexDetailsUpdateHandler(CollateralDialogFields.BASE_INDEX_VALUE)}
            validationIssues={validationIssues || undefined}
          />
        </DialogField>
      </DialogRow>
      <DialogRow>
        <DialogField>
          <TextInputField
            label={Locale.translate(Translation.TC_LAST_UPDATED_AT_DATE_AND_TIME)}
            readOnly={readOnly.lastUpdatedAtByUserOrSystem}
            onUpdate={noop}
            value={Locale.formatTimestamp(collateral.lastUpdatedAtByUserOrSystem, userPreference.dateFormat)}
          />
        </DialogField>
        <DialogField>
          <TextInputField
            label={Locale.translate(Translation.TC_LAST_UPDATED_BY)}
            readOnly={readOnly.lastUpdatedByUser}
            onUpdate={noop}
            value={getUserNameFromUserId(collateral.lastUpdatedByUser) ?? Locale.translate(Translation.SC_SYSTEM)}
          />
        </DialogField>
      </DialogRow>
    </Fragment>
  )
}
